import React, { useState } from 'react';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { useAuth } from '../../context/AuthContext.tsx';
import './LoginModal.css';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoginSuccess?: () => void;
  googleOnly?: boolean; // If true, only show Google sign-in
}

const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose, onLoginSuccess, googleOnly = false }) => {
  const { login, googleLogin } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // System login form state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  // Handle system login
  const handleSystemLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username || !password) {
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      await login({ username, password });
      setUsername('');
      setPassword('');
      onClose();

      // Call success callback if provided
      if (onLoginSuccess) {
        onLoginSuccess();
      }
    } catch (error) {
      // Error is handled in AuthContext with toast
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Google login success
  const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
    if (!credentialResponse.credential) {
      setError('No credential received from Google');
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      // Send the real Google ID token to backend
      await googleLogin(credentialResponse.credential);
      onClose();

      // Call success callback if provided
      if (onLoginSuccess) {
        onLoginSuccess();
      }
    } catch (error: any) {
      setError(error?.message || 'Google sign-in failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Google login error
  const handleGoogleError = () => {
    setError('Google sign-in was cancelled or failed. Please try again.');
  };

  if (!isOpen) return null;

  return (
    <div className="login-modal-overlay" onClick={onClose}>
      <div className="login-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="login-modal-close" onClick={onClose}>
          ×
        </button>

        <h2 className="login-modal-title">{googleOnly ? 'Sign In to Continue' : 'Welcome Back'}</h2>
        <p className="login-modal-subtitle">{googleOnly ? 'Please sign in to subscribe to a premium plan' : 'Sign in to access your account'}</p>

        {/* Error message */}
        {error && (
          <div className="login-error">
            {error}
          </div>
        )}

        {/* System User Login Form - Only show if not googleOnly */}
        {!googleOnly && (
          <>
            <form onSubmit={handleSystemLogin} className="login-form">
              <div className="login-form-group">
                <label htmlFor="username">Username</label>
                <input
                  type="text"
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter your username"
                  required
                  disabled={isLoading}
                />
              </div>

              <div className="login-form-group">
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  disabled={isLoading}
                />
              </div>

              <button
                type="submit"
                className="login-btn-primary"
                disabled={isLoading || !username || !password}
              >
                {isLoading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>

            <div className="login-divider">
              <span>OR</span>
            </div>
          </>
        )}

        {/* Google Login Button */}
        <div className="google-login-wrapper">
          {isLoading ? (
            <div className="google-loading">Signing in...</div>
          ) : (
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              useOneTap={false}
              theme="filled_black"
              size="large"
              width="100%"
              text="continue_with"
              shape="rectangular"
            />
          )}
        </div>

        {!googleOnly && (
          <div className="login-footer">
            <p>
              For system admins, use username/password.
              <br />
              Clients can sign in with Google only.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginModal;
